import { user } from 'src/StateObserver';
import { WebPaymentMethod, WebPurchaseProvider } from './iap.types';
import { orderV2Android, orderV2iOS } from './iap.ruleset';
import { getPersistent } from 'src/replicant/stats/stats.getters';
import { getSession } from '../stats/stats.getters';
import { GCInstant } from 'src/lib/gcinstant';

/**
 * Check if apple pay supported
 */
function isApplePaySupported(): boolean {
  try {
    if (
      window.ApplePaySession &&
      window.ApplePaySession.supportsVersion(3) &&
      window.ApplePaySession.canMakePayments()
    ) {
      return true;
    }
  } catch (e) {
    return false;
  }

  return false;
}

/**
 * Get available payment methods.
 * @returns Record of available payment methods
 */
export function getAvailablePaymentMethods(): {
  map: Record<WebPaymentMethod, boolean>;
  list: WebPaymentMethod[];
} {
  const isApplePayAvailable = isApplePaySupported();

  // If apple pay not available, probably google pay available on this platform,
  // in any case there will be extra validation for this if user choose google pay button in picker
  const isGooglePayAvailable = !isApplePayAvailable;

  const map = {
    komoju_credit_card: true,
    komoju_rakutenpay: true,
    komoju_paidy: false, // Should be disabled for now
    komoju_aupay: true,
    stripe_googlePay: isGooglePayAvailable,
    stripe_applePay: isApplePayAvailable,
    komoju_docomo: true,
    komoju_softbank: true,
    komoju_au: true,
    atone_bnpl: true,
    atone_lite: true,
    komoju_paypay: true,
    komoju_merpay: false,
  };

  let orderedList =
    GCInstant.osType === 'WEB_IOS' ? orderV2iOS : orderV2Android;

  return {
    map,
    list: orderedList.filter((x) => map[x]),
  };
}

export function getAvailableProviders() {
  const available = getAvailablePaymentMethods().list;

  const providers = new Set<WebPurchaseProvider>();

  const map = methodToProviderMapping();
  available.forEach((method) => {
    const provider = map[method];
    providers.add(provider);
  });

  return Array.from(providers);
}

export function getWebPaySequenceStats() {
  return {
    allTimeOpenSequence: getPersistent(user(), 'webPaySequence', 1),
    allTimeSuccessSequence: getPersistent(user(), 'webPayPurchaseSequence', 0),
    sessionOpenSequence: getSession('webPaySequence', 1),
    sessionSuccessSequence: getSession('webPayPurchaseSequence', 0),
  };
}

export const methodToProviderMapping = (): Record<
  WebPaymentMethod,
  'komoju' | 'stripe' | 'atone'
> =>
  ({
    stripe_applePay: 'stripe',
    stripe_googlePay: 'stripe',
    komoju_credit_card: 'komoju',
    komoju_paidy: 'komoju',
    komoju_aupay: 'komoju',
    komoju_rakutenpay: 'komoju',
    komoju_docomo: 'komoju',
    komoju_softbank: 'komoju',
    komoju_au: 'komoju',
    atone_bnpl: 'atone',
    atone_lite: 'atone',
    komoju_paypay: 'komoju',
    komoju_merpay: 'komoju',
  }) as const;

let childWindow: Window | null = null;
export function spawnPaymentWindow() {
  childWindow = window.open('', '_blank');

  if (childWindow) {
    // Show loading screen while the correct location is not set.
    childWindow.document.body.innerHTML = loadingScreen;

    // Override close to clear the reference.
    const originalClose = childWindow.close;
    childWindow.close = () => {
      childWindow = null;
      return originalClose.call(childWindow);
    };

    // Close the window after 5s if it's still empty.
    // Handles scenarios where the child tab opens an app or a new window, making it no longer relevant.
    // To avoid having floating tabs, the tab is destroyed whenever its visibility changes.
    const destroyWindow = () => {
      childWindow?.removeEventListener('visibilitychange', destroyWindow);
      childWindow?.close();
    };

    setTimeout(() => {
      // Tab already closed, bail out.
      if (childWindow?.closed) {
        return;
      }

      try {
        // Tab not empty, bail out.
        if (childWindow?.document.body.innerHTML !== loadingScreen) {
          return;
        }

        // If the tab is not visible, close it immediately.
        if (childWindow?.document.visibilityState === 'hidden') {
          childWindow?.close();
          return;
        }

        // If the tab is still visible, keep it open until the user tabs out.
        childWindow?.addEventListener('visibilitychange', destroyWindow);
      } catch (e) {
        // Ignore cross-origin errors. When they happen, it means the tab triggered a redirect so we should not close it.
      }
    }, 5000);
  }
}

export function getPaymentChildWindow() {
  return childWindow;
}

const loadingScreen = `
<style>
  .center {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100vw;
    height: 100vh;
  }

  .lds-ring,
  .lds-ring div {
    box-sizing: border-box;
  }
  .lds-ring {
    display: inline-block;
    position: relative;
    width: 10rem;
    height: 10rem;
  }
  .lds-ring div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: 5rem;
    height: 5rem;
    margin: 0.25rem;
    border: 0.25rem solid currentColor;
    border-radius: 50%;
    animation: lds-ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: currentColor transparent transparent transparent;
  }
  .lds-ring div:nth-child(1) {
    animation-delay: -0.45s;
  }
  .lds-ring div:nth-child(2) {
    animation-delay: -0.3s;
  }
  .lds-ring div:nth-child(3) {
    animation-delay: -0.15s;
  }
  @keyframes lds-ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>

<div class="center">
  <div class="lds-ring">
    <div></div>
    <div></div>
    <div></div>
    <div></div>
  </div>
</div>
`;

/**
 * Check if the open web purchase memory feature is enabled.
 * @returns
 */
export function isOpenWebPurchaseMemoryEnabled() {
  // Keeping this method to be used in the following quick pay test.
  return false;
}

/** Local storage key for the last selected payment method. */
const LAST_SELECTED_PAYMENT_METHOD_KEY = 'iap_last_selected_payment_method';

/**
 * Save the last selected payment method to local storage.
 * @param method
 * @returns
 */
export function saveLastSelectedPaymentMethod(method: WebPaymentMethod) {
  // Do not save the last payment method if the feature is disabled.
  if (!isOpenWebPurchaseMemoryEnabled()) {
    return;
  }

  // Only save the method if it is available.
  const available = getAvailablePaymentMethods().list;
  if (!available.includes(method)) {
    return;
  }

  // Save the selected method.
  try {
    localStorage.setItem(LAST_SELECTED_PAYMENT_METHOD_KEY, method);
  } catch (e) {
    console.warn('Failed to save last payment method', e);
  }
}

/**
 * Load the last selected payment method from local storage. Returns null if not available.
 * @returns
 */
export function getLastSelectedPaymentMethod(): WebPaymentMethod | null {
  // Do not load the last payment method if the feature is disabled.
  if (!isOpenWebPurchaseMemoryEnabled()) {
    return null;
  }

  let method: WebPaymentMethod | null = null;

  // Retrieve the last selected method.
  try {
    method = localStorage.getItem(
      LAST_SELECTED_PAYMENT_METHOD_KEY,
    ) as WebPaymentMethod;
  } catch (e) {
    console.warn('Failed to load last payment method', e);
  }

  // If there is a saved method and it is available, return it.
  if (method) {
    const available = getAvailablePaymentMethods().list;
    if (available.includes(method)) {
      return method;
    }
  }

  // Otherwise, return null.
  return null;
}

/**
 * Clear the last selected payment method from local storage.
 */
export function clearLastSelectedPaymentMethod() {
  try {
    localStorage.removeItem(LAST_SELECTED_PAYMENT_METHOD_KEY);
  } catch (e) {
    console.warn('Failed to clear last payment method', e);
  }
}
